import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { SymptomChecker } from "@/components/content/SymptomChecker";
import { FixSteps } from "@/components/content/FixSteps";
import { DIYvsDoneForYou } from "@/components/content/DIYvsDoneForYou";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { LeadFormSection } from "@/components/LeadFormSection";

const QuoteFormNotSubmitting = () => {
  const symptoms = [
    "Form submissions aren't reaching your inbox",
    "Visitors report errors when trying to submit",
    "Form shows 'sending' forever and never completes",
    "You're getting spam but not real leads",
    "Mobile users can't complete the form",
    "Form fields are difficult to tap or fill on phones"
  ];

  const rootCauses = [
    { cause: "Email delivery issues (going to spam)", impact: "Leads are submitting but you never see them" },
    { cause: "Form validation errors confusing users", impact: "Visitors don't know what they did wrong" },
    { cause: "CAPTCHA blocking legitimate submissions", impact: "Aggressive spam protection blocks real leads" },
    { cause: "PHP mail function not configured on server", impact: "Form submits but email never sends" },
    { cause: "Form too long or complex", impact: "Visitors abandon before finishing" },
    { cause: "JavaScript conflicts breaking form", impact: "Submit button doesn't work due to code errors" },
    { cause: "Mobile keyboard issues", impact: "Wrong keyboard type for phone/email fields" },
    { cause: "Hosting email limits exceeded", impact: "Server stops sending emails after quota reached" }
  ];

  const fixSteps = [
    { step: "Check spam folder and whitelist form emails", details: "Add your form's sending address to your email contacts" },
    { step: "Test the form yourself on desktop and mobile", details: "Submit a test entry and track where it goes (or doesn't)" },
    { step: "Verify your hosting email configuration", details: "Contact your host to ensure PHP mail or SMTP is working" },
    { step: "Switch to a dedicated form service", details: "Tools like Gravity Forms, WPForms, or Typeform are more reliable" },
    { step: "Set up form backup notifications", details: "Send submissions to multiple email addresses and save to database" },
    { step: "Reduce form fields to 4-5 maximum", details: "Name, phone, email, service type, and optional message" },
    { step: "Use honeypot spam protection instead of CAPTCHA", details: "Invisible spam protection that doesn't block real users" }
  ];

  const faqs = [
    { question: "Why aren't I receiving form submissions?", answer: "The most common cause is email delivery issues—forms are submitting but emails land in spam or never send due to server configuration. Check your spam folder first, then verify your hosting's email setup." },
    { question: "How do I know if my quote form is working?", answer: "Submit a test entry yourself using a different email address. Check your inbox, spam folder, and any form submission logs. If you don't receive the test entry, your form has a technical problem." },
    { question: "Why do visitors say the form isn't working?", answer: "Common causes include: validation errors with unclear messages, JavaScript conflicts preventing submission, CAPTCHA issues, or the form timing out. Test the form on different devices and browsers to replicate the issue." },
    { question: "Should I use CAPTCHA on my lawn care quote form?", answer: "Avoid traditional CAPTCHA (like reCAPTCHA checkbox) as it blocks legitimate leads. Use invisible honeypot fields instead—they catch bots without frustrating real customers." },
    { question: "How many fields should my quote form have?", answer: "4-5 fields maximum: name, phone, email, service type (dropdown), and optional message. Every additional field reduces submissions by 5-10%. Only ask for what you truly need for initial contact." },
    { question: "Why does my form work on desktop but not mobile?", answer: "Mobile forms fail when: fields are too small to tap, wrong keyboard types appear (number keyboard for phone fields), form doesn't scroll properly, or JavaScript conflicts exist on mobile browsers." },
    { question: "What's the best form plugin for lawn care websites?", answer: "WPForms and Gravity Forms are reliable choices for WordPress. They include email delivery features, spam protection, and mobile optimization. Avoid basic contact form plugins that rely on server email." },
    { question: "How do I stop form spam without blocking real leads?", answer: "Use honeypot fields (invisible to humans, traps bots), time-based validation (submissions too fast are likely bots), and email verification. These methods catch spam without inconveniencing real customers." },
    { question: "Should I save form submissions to a database?", answer: "Absolutely. Database backup ensures you never lose a lead, even if email delivery fails. Most quality form plugins include this feature. Review stored submissions weekly as a backup." },
    { question: "Can I test my form without submitting real inquiries?", answer: "Yes. Use a test email address you control, submit an entry, and track it through the entire flow. Many form plugins also have 'test mode' features that let you verify functionality without sending emails." }
  ];

  const links = [
    { to: "/websites/", label: "Website Subscription Pricing" },
    { to: "/lawn-care-website-design/", label: "Lawn Care Website Design" },
    { to: "/website-templates/lawn-care-quote-form-template/", label: "Quote Form Template" },
    { to: "/website-fixes/lawn-care-website-not-getting-leads/", label: "Website Not Getting Leads" }
  ];

  const CANONICAL = "https://www.leafrushmarketing.com/website-fixes/quote-form-not-submitting/";

  return (
    <>
      <SEO
        title="Lawn Care Quote Form Not Working? Here's the Fix"
        description="Is your lawn care quote form not submitting or reaching your inbox? Diagnose the problem and get step-by-step fixes."
        canonical={CANONICAL}
        ogUrl={CANONICAL}
      />
      <PageSchema
        url={CANONICAL}
        title="Lawn Care Quote Form Not Working? Here's the Fix"
        description="Is your lawn care quote form not submitting or reaching your inbox? Diagnose and fix it."
        faqs={faqs}
      />
      <LastUpdated />

      <article className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <header className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Lawn Care Quote Form Not Working? Here's the Fix
            </h1>
            <p className="text-xl text-muted-foreground">
              Potential customers are trying to reach you—but their submissions are vanishing. Let's fix that.
            </p>
          </header>

          <TopCTA />

          <SymptomChecker symptoms={symptoms} title="Signs Your Quote Form Has Problems" />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">Root Causes: Why Forms Fail</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {rootCauses.map((item, index) => (
                <div key={index} className="p-4 bg-muted rounded-lg">
                  <h3 className="font-semibold text-foreground">{item.cause}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{item.impact}</p>
                </div>
              ))}
            </div>
          </section>

          <MidPageCTA />

          <FixSteps steps={fixSteps} title="7 Steps to Fix Your Quote Form" />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">The Hidden Cost of a Broken Form</h2>
            <div className="prose prose-lg max-w-none text-foreground">
              <p>
                A broken quote form is the silent lead killer. Unlike a slow website where visitors leave immediately, a broken form lets visitors complete the entire journey—only to have their submission disappear.
              </p>
              <p>
                <strong>You'll never know how many leads you're losing</strong> because there's no record of failed submissions. The customer assumes you're ignoring them and calls a competitor instead.
              </p>
              <p>
                At <a href="/websites/" className="text-primary hover:underline">Leaf Rush Marketing</a>, our websites include properly configured forms with database backup, multiple notification channels, and spam protection that doesn't block real leads. Every submission reaches you.
              </p>
            </div>
          </section>

          <DIYvsDoneForYou />

          <FAQSection faqs={faqs} />

          <InternalLinks links={links} title="Related Resources" />

          <BottomCTA />

          <LeadFormSection title="Get Your Form Fixed" />
        </div>
      </article>
    </>
  );
};

export default QuoteFormNotSubmitting;
